import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  getClient, 
  getClientDevis, 
  createDevis, 
  deleteDevis,
  getEnseigneFournisseursDirectsConfig,
  setEnseigneFournisseurDirectOverride,
  deleteEnseigneFournisseurDirectOverride
} from '../services/api';
import { 
  ArrowLeft, 
  Plus, 
  FileText, 
  Trash2, 
  Eye,
  Building,
  Phone,
  Mail,
  MapPin,
  Truck,
  Edit2,
  RotateCcw,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [devisList, setDevisList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newDevis, setNewDevis] = useState({
    objet: '',
    date_remise: '',
    version: '1',
    contact_maag: '',
    surface_commerciale: 0,
    surface_annexes: 0
  });
  
  // Fournisseurs Directs config
  const [showFDConfig, setShowFDConfig] = useState(false);
  const [fdConfig, setFDConfig] = useState([]);
  const [loadingFD, setLoadingFD] = useState(false);
  const [editingFD, setEditingFD] = useState(null);
  const [editingFDValue, setEditingFDValue] = useState('');

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      const [clientRes, devisRes] = await Promise.all([
        getClient(clientId),
        getClientDevis(clientId)
      ]);
      setClient(clientRes.data);
      setDevisList(devisRes.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };
  
  // Fournisseurs Directs functions
  const loadFDConfig = async () => {
    try {
      setLoadingFD(true);
      const res = await getEnseigneFournisseursDirectsConfig(clientId);
      setFDConfig(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des fournisseurs directs');
    } finally {
      setLoadingFD(false);
    }
  };
  
  const handleToggleFDConfig = () => {
    if (!showFDConfig) {
      loadFDConfig();
    }
    setShowFDConfig(!showFDConfig);
  };
  
  const handleSaveFDOverride = async (fd) => {
    if (!editingFDValue.trim()) {
      toast.error('La désignation ne peut pas être vide');
      return;
    }
    try {
      await setEnseigneFournisseurDirectOverride(clientId, {
        base_fd_id: fd.id,
        designation: editingFDValue.trim(),
        actif: true
      });
      toast.success('Personnalisation enregistrée');
      setEditingFD(null);
      loadFDConfig();
    } catch (e) {
      toast.error('Erreur');
    }
  };
  
  const handleResetFDToBase = async (fd) => {
    if (!fd.override_id) return;
    if (!window.confirm('Revenir à la désignation de base ?')) return;
    try {
      await deleteEnseigneFournisseurDirectOverride(clientId, fd.override_id);
      toast.success('Réinitialisé à la base');
      loadFDConfig();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleCreateDevis = async (e) => {
    e.preventDefault();
    try {
      const res = await createDevis({
        client_id: clientId,
        ...newDevis
      });
      toast.success('Devis créé avec succès');
      setShowModal(false);
      navigate(`/devis/${res.data.id}`);
    } catch (e) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteDevis = async (id, objet) => {
    if (!window.confirm(`Supprimer le devis "${objet}" ?`)) return;
    try {
      await deleteDevis(id);
      toast.success('Devis supprimé');
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="client-detail-page">
      <div className="mb-6">
        <Link 
          to="/clients" 
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Retour aux clients
        </Link>
      </div>

      {/* Client info card */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm mb-8">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center mr-4">
              <Building className="w-7 h-7 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{client.nom}</h1>
              {client.contact && (
                <p className="text-slate-500">{client.contact}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="new-devis-client-btn"
            className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau devis
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-600">
          {client.adresse && (
            <span className="flex items-center">
              <MapPin className="w-4 h-4 mr-2 text-slate-400" />
              {client.adresse}
            </span>
          )}
          {client.telephone && (
            <span className="flex items-center">
              <Phone className="w-4 h-4 mr-2 text-slate-400" />
              {client.telephone}
            </span>
          )}
          {client.email && (
            <span className="flex items-center">
              <Mail className="w-4 h-4 mr-2 text-slate-400" />
              {client.email}
            </span>
          )}
        </div>
      </div>

      {/* Fournisseurs Directs Configuration */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm mb-6 overflow-hidden">
        <button
          onClick={handleToggleFDConfig}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center">
            <Truck className="w-5 h-5 text-purple-600 mr-3" />
            <div className="text-left">
              <span className="font-medium text-slate-800">Fournisseurs Directs personnalisés</span>
              <p className="text-xs text-slate-500">Personnaliser les désignations pour cette enseigne</p>
            </div>
          </div>
          <span className={`transform transition-transform ${showFDConfig ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
        
        {showFDConfig && (
          <div className="border-t border-slate-200 p-6">
            {loadingFD ? (
              <div className="text-center py-8 text-slate-500">Chargement...</div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-500 mb-4">
                  Modifiez les désignations pour cette enseigne. Les modifications n'affectent que les projets de <strong>{client?.nom}</strong>.
                </p>
                
                {fdConfig.map((fd) => (
                  <div key={fd.id} className={`flex items-center gap-3 p-3 rounded-lg ${fd.has_override ? 'bg-purple-50 border border-purple-200' : 'bg-slate-50'}`}>
                    <div className="flex-1">
                      {editingFD === fd.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingFDValue}
                            onChange={(e) => setEditingFDValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveFDOverride(fd);
                              if (e.key === 'Escape') setEditingFD(null);
                            }}
                            className="flex-1 h-9 px-3 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveFDOverride(fd)}
                            className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingFD(null)}
                            className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <span className={`text-sm ${fd.has_override ? 'font-medium text-purple-800' : 'text-slate-700'}`}>
                            {fd.designation}
                          </span>
                          {fd.has_override && (
                            <span className="ml-2 text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded">
                              Personnalisé
                            </span>
                          )}
                          {fd.has_override && fd.base_designation !== fd.designation && (
                            <span className="ml-2 text-xs text-slate-400">
                              (base: {fd.base_designation})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {editingFD !== fd.id && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditingFD(fd.id);
                            setEditingFDValue(fd.designation);
                          }}
                          className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                          title="Personnaliser"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {fd.has_override && (
                          <button
                            onClick={() => handleResetFDToBase(fd)}
                            className="p-2 text-orange-500 hover:bg-orange-50 rounded-lg"
                            title="Revenir à la base"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Devis list */}
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        Devis ({devisList.length})
      </h2>

      {devisList.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">Aucun devis pour ce client</p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center mt-4 text-orange-500 hover:text-orange-600"
          >
            <Plus className="w-4 h-4 mr-1" /> Créer le premier devis
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Objet</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Version</th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Total HT</th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Total TTC</th>
                <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {devisList.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`devis-row-${d.id}`}>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">{d.objet}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">v{d.version}</td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {d.date_remise || new Date(d.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-900 text-right font-mono">
                    {formatMoney(d.total_ht)}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-900 text-right font-mono font-medium">
                    {formatMoney(d.total_ttc)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Link
                        to={`/devis/${d.id}`}
                        data-testid={`view-devis-${d.id}`}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDeleteDevis(d.id, d.objet)}
                        data-testid={`delete-devis-${d.id}`}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4" data-testid="new-devis-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Nouveau devis pour {client.nom}</h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-slate-100 rounded text-slate-500"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreateDevis} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Objet du devis *
                </label>
                <input
                  data-testid="devis-objet-input"
                  type="text"
                  value={newDevis.objet}
                  onChange={(e) => setNewDevis({...newDevis, objet: e.target.value})}
                  placeholder="Ex: Rénovation magasin Lyon"
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Date de remise
                  </label>
                  <input
                    data-testid="devis-date-input"
                    type="date"
                    value={newDevis.date_remise}
                    onChange={(e) => setNewDevis({...newDevis, date_remise: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Version
                  </label>
                  <input
                    data-testid="devis-version-input"
                    type="text"
                    value={newDevis.version}
                    onChange={(e) => setNewDevis({...newDevis, version: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact MAAG
                </label>
                <input
                  data-testid="devis-contact-input"
                  type="text"
                  value={newDevis.contact_maag}
                  onChange={(e) => setNewDevis({...newDevis, contact_maag: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Surface commerciale (m²)
                  </label>
                  <input
                    data-testid="devis-surface-input"
                    type="number"
                    value={newDevis.surface_commerciale}
                    onChange={(e) => setNewDevis({...newDevis, surface_commerciale: parseFloat(e.target.value) || 0})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Surface annexes (m²)
                  </label>
                  <input
                    data-testid="devis-annexes-input"
                    type="number"
                    value={newDevis.surface_annexes}
                    onChange={(e) => setNewDevis({...newDevis, surface_annexes: parseFloat(e.target.value) || 0})}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 h-10 border border-slate-300 text-slate-700 rounded-md font-medium hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  data-testid="create-devis-btn"
                  className="flex-1 h-10 bg-slate-900 text-white rounded-md font-medium hover:bg-slate-800 transition-colors"
                >
                  Créer le devis
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetailPage;
